import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import BaseCard from "../../components/ui/BaseCard";
import BaseDataTableV2 from "../../components/ui/BaseDataTableV2";
import BaseTextField from "../../components/ui/BaseTextField";
import BaseButton from "../../components/ui/BaseButton";
import BaseDropdownV2 from "../../components/ui/BaseDropdownV2";

import { fetchAllVendors, fetchCategories } from "../../services/api/category";
import AddEditVendorModal from "./modals/AddEditVendorModal";
import { debounce } from "../../utils/helpers";

const menuOptions = [];

export default function VendorsTable() {
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [rowCountState, setRowCountState] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });

  const [isVendorModalOpen, setVendorModalOpen] = useState(false);
  const [venMode, setVenMode] = useState("add");
  const [venInitial, setVenInitial] = useState(null);
  const params = useMemo(
    () => ({
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
      search: searchQuery || undefined,
    }),
    [paginationModel, searchQuery]
  );

  const {
    data: vendorsData,
    isPending,
    refetch,
  } = useQuery({
    queryKey: ["vendors", params.page, params.limit, params.search],
    queryFn: () => fetchAllVendors(params),
    keepPreviousData: true,
  });

  useEffect(() => {
    setRowCountState((prev) => vendorsData?.total ?? prev);
  }, [vendorsData?.total]);

  const { data: categoriesData } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });

  const categories = useMemo(
    () => categoriesData?.data ?? [],
    [categoriesData]
  );

  const rows = useMemo(() => {
    const list = vendorsData?.data ?? [];
    return list.map((v) => ({
      id: v.id,
      vendorName: v.name ?? "-",
      categoryName: v.category?.category ?? "-",
      categoryId: v.category?.id ?? v.categoryId ?? null,
    }));
  }, [vendorsData]);

  const openAdd = () => {
    setVenMode("add");
    setVenInitial(null);
    setVendorModalOpen(true);
  };

  const openEdit = (row) => {
    setVenMode("edit");
    setVenInitial({
      id: row.id,
      name: row.vendorName,
      categoryId: row.categoryId,
    });
    setVendorModalOpen(true);
  };

  const handleMenuClick = (item, row) => {
    if (item.value === "EDIT") openEdit(row);
    if (item.value === "DELETE") {
    }
  };

  const debouncedSetSearchQuery = useRef(
    debounce((val) => {
      setSearchQuery(val);
      setPaginationModel((m) => ({ ...m, page: 0 }));
    }, 400)
  );

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    debouncedSetSearchQuery.current(val);
  };

  const columns = [
    {
      field: "vendorName",
      headerName: "Category",
      flex: 1,
      renderCell: (params) => (
        <button className="text-primary">{params.value}</button>
      ),
    },
    {
      field: "categoryName",
      headerName: "Technology Stack",
      flex: 1,
      renderCell: (params) => {
        return <span>{params.value}</span>;
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.6,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <BaseDropdownV2
          menuOptions={menuOptions}
          triggerElement={
            <i className="fa-solid fa-ellipsis cursor-pointer ml-4 text-base" />
          }
          onItemClick={(item) => handleMenuClick(item, params.row)}
        />
      ),
    },
  ];

  return (
    <>
      <div className="bg-white rounded-lg px-5 py-3.5 shadow-md mt-4 sticky top-10 z-10">
        <div className="flex justify-between items-center w-full gap-4">
          <div className="flex items-center border-r-[1px] border-[#8C8C8C] pr-5">
            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary"><i className="fa-light fa-filter text-white text-sm"></i></span>
            <p className="text-primary ml-2">Add Filter</p>
          </div>

          <div className="w-[76%]">
            <BaseTextField
              placeholder="Search categories or technology stacks"
              value={searchInput}
              onChange={handleSearch}
              startAdornment={
                <i className="fa-light fa-magnifying-glass text-lg" />
              }
            />
          </div>

          <BaseButton
            className="md:w-auto w-full"
            variant="contained"
            color="primary"
            style={{ padding: "6px 12px" }}
            onClick={openAdd}
          >
            Create New Category
          </BaseButton>
        </div>
      </div>

      <BaseCard
        className="rounded-tl-xl rounded-tr-xl shadow-lg mt-5"
        elevation={0}
      >
            <BaseDataTableV2
              className="max-h-[calc(100vh-320px)] overflow-auto"
              columns={columns}
              data={rows}
              checkboxSelection
              isLoading={isPending}
              hideFooter={false}
              pagination
              paginationMode="server"
              rowCount={rowCountState}
              pageSizeOptions={[10, 20, 50, 100]}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
            />
      </BaseCard>

      {isVendorModalOpen && (
        <AddEditVendorModal
          open={isVendorModalOpen}
          mode={venMode}
          initialData={venInitial}
          categories={categories}
          close={() => setVendorModalOpen(false)}
          refetchVendors={refetch}
        />
      )}
    </>
  );
}
